import React, { Suspense, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Drawer from '../Common/Drawer/Drawer';
import Toggle from '../Common/Toggle/Toggle';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import type { ColumnOption } from '../../apis/columnDrawer/columnDrawer.types';

// Error Fallback Component
const ErrorFallback: React.FC<{ error: unknown; resetErrorBoundary: () => void }> = ({ 
	error, 
	resetErrorBoundary 
}) => (
	<div className="alert alert-danger m-3">
		<h5>Something went wrong loading column drawer:</h5>
		<p>{error instanceof Error ? error.message : String(error)}</p>
		<button className="btn btn-secondary" onClick={resetErrorBoundary}>
			Try again
		</button>
	</div>
);

// Loading Component for Suspense
const LoadingComponent: React.FC = () => (
	<LoadingIndicator isLoading={true}>
		<div>Loading columns...</div>
	</LoadingIndicator>
);

// Component Props Interface
interface ComponentProps {
	onComplete: () => void;
	onColumnToggle: (field: string, enabled: boolean) => void;
	columnOptions: Array<{ 
		label: string; 
		value: ColumnOption; 
	}>;
	columns: ColumnOption[];
}

// Main Content Component
const BaselineTaskListColumnDrawerContent: React.FC<ComponentProps> = ({
	onComplete,
	onColumnToggle,
	columnOptions,
	columns,
}) => {
	// Check if a column is currently active
	const columnIsActive = useCallback(
		(column: { label: string; value: ColumnOption }): boolean => {
			return columns.some((c) => c.field === column.value.field);
		}, 
		[columns]
	);

	// Handle form change events
	const onFormChange = useCallback(
		(field: string, enabled: boolean) => {
			onColumnToggle(field, enabled);
		}, 
		[onColumnToggle]
	);

	return (
		<Drawer 
			isWide={false} 
			title="Columns" 
			cancelText="Close" 
			onCancelClick={onComplete}
		>
			{columnOptions.map((column) => (
				<div key={column.label}>
					<Toggle 
						name={column.value.field}
						text={column.label}
						checked={columnIsActive(column)}
						onChange={onFormChange}
					/>
				</div>
			))}
		</Drawer>
	);
};

// Main Export Component with Error Boundary and Suspense
const BaselineTaskListColumnDrawer: React.FC<ComponentProps> = (props) => {
	return (
		<ErrorBoundary
			FallbackComponent={ErrorFallback}
			onReset={() => window.location.reload()}
		>
			<Suspense fallback={<LoadingComponent />}>
				<BaselineTaskListColumnDrawerContent {...props} />
			</Suspense>
		</ErrorBoundary>
	);
};

export default BaselineTaskListColumnDrawer;